import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Title3, Button } from '@fluentui/react-components';
import { Add20Regular } from '@fluentui/react-icons';
import EventGrid from './components/EventGrid';
import { useEvent } from '../../hooks/useEvent';
import { Event } from '../../types/event';
import ConfirmDialog from '../../components/ConfirmDialog';

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalL,
  },
  title: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  content: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    padding: tokens.spacingHorizontalXXL,
  },
});

const EventManagement: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [eventToDelete, setEventToDelete] = React.useState<Event | null>(null);

  const { events, listLoading, listError, deleteEvent, fetchEvents } = useEvent();

  // Carregar eventos ao montar o componente
  React.useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleAdd = () => {
    navigate('/events/new');
  };

  const handleEdit = (event: Event) => {
    navigate(`/events/edit?id=${event.id}`);
  };

  const handleDelete = (id: number) => {
    const event = events.find((e: Event) => e.id === id);
    if (event) {
      setEventToDelete(event);
      setDeleteDialogOpen(true);
    }
  };

  const handleConfirmDelete = async () => {
    if (eventToDelete) {
      try {
        await deleteEvent(eventToDelete.id);
        // Após exclusão bem-sucedida, atualizar a lista
        fetchEvents();
      } catch (error) {
        // Erro será tratado pelo estado error do hook
        console.error('Erro ao excluir evento:', error);
      }
    }
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setEventToDelete(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Title3 className={styles.title}>Eventos</Title3>
        <Button
          appearance="primary"
          size="large"
          icon={<Add20Regular />}
          onClick={handleAdd}
          className={styles.addButton}
        >
          Novo Evento
        </Button>
      </div>

      <div className={styles.content}>
        <EventGrid
          events={events}
          loading={listLoading}
          error={listError?.message || null}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </div>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Excluir Evento"
        message={`Tem certeza que deseja excluir o evento "${eventToDelete?.descricao}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        intent="danger"
      />
    </div>
  );
};

export default EventManagement;